import { Eye, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { useAuth } from '@/contexts/AuthContext';
import { useVisionTrigger } from '@/hooks/useVisionTrigger';
import { formatSubmissionDate } from '@/lib/utils';

export function VisionProducts() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.General.components.VisionProducts',
  });

  const { user } = useAuth();

  const visionAutoRun = !!user?.customer.visionAutoRun;
  const isChecked = visionAutoRun;

  const { updateNeedVisionRun } = useVisionTrigger();

  async function handleToggleAutoRun() {
    try {
      const data = await updateNeedVisionRun({ visionAutoRun: !isChecked });

      toast(
        data.visionAutoRun
          ? t('toast.enableAutoRun')
          : t('toast.disableAutoRun'),
      );
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-4">
          <Label
            htmlFor="vision-toggle"
            className="flex flex-col space-y-1 text-lg font-semibold"
          >
            {t('title')}
          </Label>

          <Switch
            id="vision-toggle"
            checked={isChecked}
            onCheckedChange={handleToggleAutoRun}
          />
        </div>

        <p className="text-sm text-muted-foreground">{t('enableLabel')}</p>
      </div>

      <div className="flex items-center gap-4">
        <p className="text-xs text-muted-foreground">
          {t('lastUpdateLabel')}
          <strong>
            {formatSubmissionDate(user?.customer.visionLastSubmission)}
          </strong>
        </p>

        <div className="flex items-center space-x-2">
          <Eye
            className={`h-4 w-4 ${isChecked ? 'text-green-500' : 'text-gray-400'}`}
          />

          <span
            className={`text-sm ${isChecked ? 'text-green-500' : 'text-gray-400'}`}
          >
            {isChecked ? t('activeLabel') : t('inactiveLabel')}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <TriangleAlert size={20} className="text-yellow-500" />

        <p className="text-sm">{t('warningLabel')}</p>
      </div>
    </div>
  );
}
